import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { PurchaseOrder } from '@/lib/models/purchaseOrder';
import type { PurchaseOrderIntegration } from '@/lib/models/purchaseOrderIntegration';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per gli endpoints dei PurchaseOrder
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Tutti i metodi restituiscono Promise con FetchResponse wrappato
 */
export interface PurchaseOrderEndpoints {
	/**
	 * Recupera la lista degli ordini di acquisto con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=code asc")
	 * @returns Promise con FetchResponse contenente ODataResponse<PurchaseOrder>
	 */
	getPurchaseOrders(query: string): Promise<FetchResponse<ODataResponse<PurchaseOrder>>>;

	/**
	 * Recupera un singolo ordine di acquisto per ID
	 * @param id - ID dell'ordine di acquisto
	 * @returns Promise con FetchResponse contenente PurchaseOrder
	 */
	getPurchaseOrder(id: number): Promise<FetchResponse<PurchaseOrder>>;

	/**
	 * Aggiorna un ordine di acquisto esistente
	 * @param id - ID dell'ordine di acquisto da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con FetchResponse contenente PurchaseOrder aggiornato
	 */
	updatePurchaseOrder(id: number, payload: PurchaseOrder): Promise<FetchResponse<PurchaseOrder>>;

	/**
	 * Crea un nuovo ordine di acquisto
	 * @param payload - Dati del nuovo ordine di acquisto
	 * @returns Promise con FetchResponse contenente PurchaseOrder creato
	 */
	postPurchaseOrder(payload: PurchaseOrder): Promise<FetchResponse<PurchaseOrder>>;

	/**
	 * Elimina un ordine di acquisto
	 * @param id - ID dell'ordine di acquisto da eliminare
	 * @returns Promise con FetchResponse contenente void
	 */
	deletePurchaseOrder(id: number): Promise<FetchResponse<void>>;

	/**
	 * Recupera le integrazioni per un ordine di acquisto
	 * @param purchaseOrderId - ID dell'ordine di acquisto
	 * @returns Promise con FetchResponse contenente ODataResponse<PurchaseOrderIntegration>
	 */
	getIntegrations(
		purchaseOrderId: number,
	): Promise<FetchResponse<ODataResponse<PurchaseOrderIntegration>>>;
}

/**
 * Implementazione degli endpoints per i PurchaseOrder
 * Mantiene esattamente gli stessi nomi dei metodi e URL del JavaScript originale
 * Wrappa le chiamate API con tipi TypeScript appropriati
 */
const purchaseOrderEndpoints: PurchaseOrderEndpoints = {
	getPurchaseOrders(query: string): Promise<FetchResponse<ODataResponse<PurchaseOrder>>> {
		return api.get(`/odata/v1/PurchaseOrders${query}`);
	},

	getPurchaseOrder(id: number): Promise<FetchResponse<PurchaseOrder>> {
		return api.get(`/odata/v1/PurchaseOrders/${id}`);
	},

	updatePurchaseOrder(id: number, payload: PurchaseOrder): Promise<FetchResponse<PurchaseOrder>> {
		return api.put(`/api/v1/PurchaseOrders/${id}`, payload);
	},

	postPurchaseOrder(payload: PurchaseOrder): Promise<FetchResponse<PurchaseOrder>> {
		return api.post('/api/v1/PurchaseOrders', payload);
	},

	deletePurchaseOrder(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/PurchaseOrders/${id}`);
	},

	getIntegrations(
		purchaseOrderId: number,
	): Promise<FetchResponse<ODataResponse<PurchaseOrderIntegration>>> {
		return api.get(`/odata/v1/PurchaseOrderIntegrations?$filter=(sourceId eq ${purchaseOrderId})`);
	},
};

export default purchaseOrderEndpoints;
